import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { v4 as uuid } from 'uuid';
import {
  ArrowLeftIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  TrashIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Badge from '@/components/ui/Badge';
import Switch from '@/components/ui/Switch';
import Select from '@/components/ui/Select';
import SegmentedControl from '@/components/ui/SegmentedControl';
import { getCycles, type CycleRole } from '@/api/cyclesService';
import { getPlan, createPlan, updatePlan, PlanPayload, PlanSportVolume, PlanType, DayMode } from '@/api/plansService';
import { useFormats, useRaceTypes, useSports } from '@/features/reference/useReference';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';
import { planCycleColor } from './planColors';
import type { SequenceRow, CycleMeta } from './planComposerTypes';
import CycleLengthModal from './CycleLengthModal';
import AllocationBars from './AllocationBars';
import GenerationCheckPanel from './GenerationCheckPanel';
import SportVolumesTable from './SportVolumesTable';
import {
  allocationOf,
  BEGINNER_FORMAT,
  dmaxWeeks,
  dminWeeks,
  FORMAT_SUBTITLES,
  PLAN_TYPE_LABELS,
  planTypeOfRaceType,
  recoveryWeeks,
  type CycleAllocation,
} from './planAllocation';

/** R39: a generalist is exactly 53 weeks, the most an ISO year holds. */
const GENERIC_WEEKS = 53;
const PPG_SPORT = 'PPG';

/** Fallback bounds for a sport that has just entered the plan (PRD 2 §5.1). */
const defaultVolume = (sportId: string) => ({ sportId, min: 1, max: 3, default: 2 });

const ROLE_BADGES: Partial<Record<CycleRole, { label: string; title: string; className: string }>> = {
  TEST: {
    label: 'Cycle de tests',
    title: "Cycle de tests : l'athlète qui coupe le switch « tests » garde seulement les autres séances de sa première occurrence.",
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  RECOVERY: {
    label: 'Récupération',
    title: 'Joué après la course, en fin de plan spé. Jamais réduit ni condensé.',
    className: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  },
};

const RailCard = ({
  id,
  name,
  minWeeks,
  maxWeeks,
  used,
}: {
  id: string;
  name: string;
  minWeeks: number;
  maxWeeks: number;
  used: boolean;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `cyc:${id}`,
    data: { cycleId: id },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={clsx(
        'flex cursor-grab flex-col gap-1 rounded-lg border p-2.5 text-left transition-colors',
        isDragging && 'opacity-40',
        'border-gray-200 bg-white hover:border-brand-300 dark:border-gray-800 dark:bg-gray-900'
      )}
    >
      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{name}</span>
      <span className="flex items-center gap-2 text-xs text-gray-500">
        {minWeeks}–{maxWeeks} semaines
        <Badge color={used ? 'blue' : 'gray'}>{used ? 'UTILISÉ' : 'VIDE'}</Badge>
      </span>
    </div>
  );
};

const PlanComposerPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uuid: planId } = useParams();
  const [searchParams] = useSearchParams();
  const sourceId = searchParams.get('from');
  const isEdit = Boolean(planId);
  /** `/plans/create?from=<id>`: the source is loaded into the form but nothing is saved yet. */
  const isDuplicate = !isEdit && Boolean(sourceId);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const [name, setName] = useState('');
  const [raceTypeId, setRaceTypeId] = useState<number | null>(null);
  const [formatId, setFormatId] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [dayMode, setDayMode] = useState<DayMode>('ATHLETE');
  const [volumes, setVolumes] = useState<Record<string, PlanSportVolume>>({});
  const [condensedMinWeeks, setCondensedMinWeeks] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [rows, setRowsState] = useState<SequenceRow[]>([]);
  const setRows: typeof setRowsState = (update) => {
    setDirty(true);
    setRowsState(update);
  };
  const [meta, setMeta] = useState<Record<string, CycleMeta>>({});
  const [search, setSearch] = useState('');
  const [dragging, setDragging] = useState<string | null>(null);
  const [lengthUid, setLengthUid] = useState<string | null>(null);

  const { data: formats } = useFormats();
  const { data: raceTypes } = useRaceTypes();
  const { data: sportsRef } = useSports();
  const raceType = raceTypes?.find((rt) => rt.id === raceTypeId);
  // D-a: the plan type follows the race type; D-b: a Débutant plan reads the BEGINNER ranks.
  const planType: PlanType = planTypeOfRaceType(raceType?.identifier);
  const isBeginner = planType === 'reprise';
  const typeFormats = (formats ?? []).filter((f) => f.raceTypeId === raceTypeId);
  const format = isBeginner ? undefined : typeFormats.find((f) => f.id === formatId);
  const formatIdentifier = isBeginner ? BEGINNER_FORMAT : format?.identifier ?? '';
  const derivedName = [raceType?.label, format?.label].filter(Boolean).join(' ');

  const { data: rail } = useQuery({
    queryKey: ['admin', 'pv2', 'plan-rail', search],
    queryFn: () => getCycles({ search: search || undefined, pageSize: 100 }),
  });

  const { data: planDetail } = useQuery({
    queryKey: queryKeys.pv2Plan(planId ?? sourceId ?? ''),
    queryFn: () => getPlan((planId ?? sourceId)!),
    enabled: isEdit || isDuplicate,
  });

  useEffect(() => {
    if (raceTypeId === null && raceTypes && raceTypes.length > 0 && !isEdit && !isDuplicate) setRaceTypeId(raceTypes[0].id);
  }, [raceTypes, raceTypeId, isEdit, isDuplicate]);

  useEffect(() => {
    if (!isBeginner && !format && typeFormats.length > 0) setFormatId(typeFormats[0].id);
  }, [isBeginner, format, typeFormats]);

  useEffect(() => {
    if (rail?.items) {
      setMeta((prev) => {
        const next = { ...prev };
        for (const c of rail.items) {
          next[c.id] = {
            name: c.name,
            role: c.role,
            minWeekNumber: c.minWeekNumber,
            maxWeekNumber: c.maxWeekNumber,
            priorityByFormat: c.priorityByFormat,
            sports: c.sports,
          };
        }
        return next;
      });
    }
  }, [rail]);

  useEffect(() => {
    if (!planDetail) return;
    setName(isDuplicate ? `Copie de ${planDetail.displayName}` : planDetail.name ?? '');
    setRaceTypeId(planDetail.raceTypeId);
    setFormatId(planDetail.formatId ?? '');
    setIsAvailable(planDetail.isAvailable);
    setDayMode(planDetail.dayMode);
    setCondensedMinWeeks(planDetail.condensedMinWeeks);
    setVolumes(Object.fromEntries(planDetail.sportVolumes.map((v) => [v.sportId, v])));
    setRowsState(planDetail.cycles.map((c) => ({ uid: uuid(), cycleId: c.cycleId, weekCount: c.weekCount })));
    setDirty(isDuplicate);
    setMeta((prev) => {
      const next = { ...prev };
      for (const c of planDetail.cycles) {
        next[c.cycleId] = {
          name: c.name,
          role: c.role,
          minWeekNumber: c.minWeekNumber,
          maxWeekNumber: c.maxWeekNumber,
          priorityByFormat: c.priorityByFormat,
          sports: prev[c.cycleId]?.sports,
        };
      }
      return next;
    });
  }, [planDetail, isDuplicate]);

  const saveMutation = useMutation({
    mutationFn: (payload: PlanPayload) =>
      isEdit ? updatePlan(planId!, payload) : createPlan(payload),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.pv2Plan(saved.id), saved);
      queryClient.invalidateQueries({ queryKey: ['admin', 'pv2', 'plans'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: queryKeys.pv2PlanKpis, refetchType: 'all' });
      toaster.create({ title: isEdit ? 'Plan mis à jour' : 'Plan créé', type: 'success' });
      setDirty(false);
      if (!isEdit) navigate(`/plans/edit/${saved.id}`);
    },
    onError: (error) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Enregistrement impossible.';
      toaster.create({ title: 'Enregistrement impossible', description: message, type: 'error' });
    },
  });

  const addCycle = (cycleId: string) => {
    if (!meta[cycleId]) return;
    setRows((prev) => [...prev, { uid: uuid(), cycleId, weekCount: null }]);
  };

  const removeRow = (uid: string) => setRows((prev) => prev.filter((r) => r.uid !== uid));

  const moveRow = (uid: string, dir: -1 | 1) =>
    setRows((prev) => {
      const i = prev.findIndex((r) => r.uid === uid);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const setWeekCount = (uid: string, weekCount: number | null) =>
    setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, weekCount } : r)));

  const changeFormat = (id: string) => {
    setDirty(true);
    setFormatId(id);
  };

  const changeRaceType = (value: string) => {
    const next = raceTypes?.find((rt) => String(rt.id) === value);
    if (!next) return;
    setDirty(true);
    setRaceTypeId(next.id);
    setFormatId((formats ?? []).find((f) => f.raceTypeId === next.id)?.id ?? '');
    if (planTypeOfRaceType(next.identifier) !== 'spe') setCondensedMinWeeks(null);
  };

  const onDragStart = (e: DragStartEvent) => setDragging((e.active.data.current?.cycleId as string) ?? null);
  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    const cycleId = e.active.data.current?.cycleId as string | undefined;
    if (cycleId && e.over?.id === 'seq') addCycle(cycleId);
  };

  const sportLabels: Record<string, string> = Object.fromEntries((sportsRef ?? []).map((s) => [s.identifier, s.label]));

  // The sessions-per-week table lists exactly the non-PPG sports of the sequenced cycles.
  const volumeSports = [
    ...new Set(rows.flatMap((r) => meta[r.cycleId]?.sports ?? []).filter((sportId) => sportId !== PPG_SPORT)),
  ].sort((a, b) => (sportLabels[a] ?? a).localeCompare(sportLabels[b] ?? b));

  const setVolume = (sportId: string, next: PlanSportVolume) => {
    setDirty(true);
    setVolumes((prev) => ({ ...prev, [sportId]: next }));
  };

  const alloc = allocationOf(rows, meta, formatIdentifier);
  const dmin = dminWeeks(alloc);
  const dmax = dmaxWeeks(alloc);
  const recovery = recoveryWeeks(alloc);
  const thresholdOptions = Array.from({ length: Math.max(0, dmin / 2) }, (_, i) => (i + 1) * 2);

  const handleSave = () => {
    if (raceTypeId === null) {
      toaster.create({ title: 'Type de course requis', description: 'Choisissez un type de course.', type: 'error' });
      return;
    }
    if (!isBeginner && !format) {
      toaster.create({ title: 'Format requis', description: 'Choisissez un format.', type: 'error' });
      return;
    }
    const payload: PlanPayload = {
      name: name.trim() || null,
      raceTypeId,
      formatId: isBeginner ? null : formatId,
      isAvailable,
      dayMode,
      condensedMinWeeks: planType === 'spe' ? condensedMinWeeks : null,
      cycles: rows.map((r, i) => ({ cycleId: r.cycleId, orderIndex: i, weekCount: r.weekCount })),
      // R47: ni `FIXED` ni `DAYS_ONLY` ne demandent un nombre de séances à l'athlète.
      sportVolumes: dayMode === 'ATHLETE' ? volumeSports.map((sportId) => volumes[sportId] ?? defaultVolume(sportId)) : [],
    };
    saveMutation.mutate(payload);
  };

  const lengthRow = rows.find((r) => r.uid === lengthUid) ?? null;
  const lengthPosition = lengthRow ? rows.findIndex((r) => r.uid === lengthRow.uid) + 1 : 0;
  const draggingMeta = dragging ? meta[dragging] : null;

  return (
    <>
      <PageHeader
        title={isEdit ? 'Éditer le plan' : isDuplicate ? 'Dupliquer le plan' : 'Nouveau plan'}
        description={
          isDuplicate
            ? 'Copie non enregistrée — ajustez-la puis cliquez sur « Enregistrer le plan ».'
            : 'Séquencez des cycles et choisissez leur longueur.'
        }
        action={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate('/plans')}>
              <ArrowLeftIcon className="h-4 w-4" /> Annuler
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer le plan'}
            </Button>
          </div>
        }
      />

      <Card className="mb-4 flex flex-col gap-6">
        <div className="flex flex-col gap-6">
          {isEdit && planDetail && !planDetail.isGeneratable && (
            <p className="rounded-lg bg-warning-500/10 px-3 py-2 text-sm text-warning-600">
              Plan à corriger : vérifiez son type de course et son format, puis enregistrez-le. Il n'est pas proposé aux
              athlètes d'ici là.
            </p>
          )}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">Type de course *</p>
            <SegmentedControl
              value={raceTypeId === null ? '' : String(raceTypeId)}
              onChange={changeRaceType}
              options={(raceTypes ?? []).map((rt) => ({ value: String(rt.id), label: rt.label }))}
            />
            <p className="mt-1 text-xs text-gray-400">
              Type de plan : {PLAN_TYPE_LABELS[planType]}
              {planType === 'spe' && ' — doit se terminer par un ou plusieurs cycles de rôle « Récupération », contigus et en dernier.'}
            </p>
          </div>
          {!isBeginner && (
            <div>
              <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">Format *</p>
              <div className="flex flex-wrap gap-2">
                {typeFormats.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => changeFormat(f.id)}
                    className={clsx(
                      'flex flex-col items-start rounded-xl border px-4 py-2 text-left transition-colors',
                      formatId === f.id
                        ? 'border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-300'
                        : 'border-gray-200 text-gray-600 hover:border-brand-300 dark:border-gray-800 dark:text-gray-300'
                    )}
                  >
                    <span className="text-sm font-semibold">{f.label}</span>
                    <span className="text-[11px] opacity-70">{FORMAT_SUBTITLES[f.identifier] ?? f.slug}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="w-full max-w-md">
            <Input
              label="Nom du plan"
              placeholder={derivedName ? `Vide : « ${derivedName} »` : 'Optionnel'}
              value={name}
              onChange={(e) => {
                setDirty(true);
                setName(e.target.value);
              }}
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Disponible pour les athlètes</span>
            <Switch
              checked={isAvailable}
              onChange={(v) => {
                setDirty(true);
                setIsAvailable(v);
              }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">Jours d'entraînement *</p>
            <SegmentedControl
              value={dayMode}
              onChange={(v) => {
                setDirty(true);
                setDayMode(v as DayMode);
              }}
              options={[
                { value: 'ATHLETE', label: "Choisis par l'athlète" },
                { value: 'DAYS_ONLY', label: 'Jours seuls' },
                { value: 'FIXED', label: 'Fixes' },
              ]}
            />
            {dayMode === 'FIXED' && <p className="mt-1 text-xs text-gray-400">Les jours définis dans les cycles sont imposés.</p>}
            {dayMode === 'DAYS_ONLY' && (
              <p className="mt-1 text-xs text-gray-400">
                L'athlète donne ses jours disponibles, toutes les séances de la semaine sont conservées. Pas d'enchaînement dans ce mode.
              </p>
            )}
          </div>
          {dayMode === 'ATHLETE' && (
            <div className="flex-1">
              <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">Séances par semaine *</p>
              <SportVolumesTable sports={volumeSports} volumes={volumes} labels={sportLabels} onChange={setVolume} />
            </div>
          )}
          {planType === 'spe' && (
            <div className="flex-1">
              <div className="mb-2 flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Autoriser une préparation condensée</span>
                <Switch
                  checked={condensedMinWeeks !== null}
                  onChange={(v) => {
                    setDirty(true);
                    setCondensedMinWeeks(v ? Math.max(2, dmin - 2) || 2 : null);
                  }}
                />
              </div>
              {condensedMinWeeks !== null && (
                <Select
                  label="Durée minimale acceptée"
                  value={String(condensedMinWeeks)}
                  onChange={(e) => {
                    setDirty(true);
                    setCondensedMinWeeks(Number(e.target.value));
                  }}
                >
                  {(thresholdOptions.length > 0 ? thresholdOptions : [condensedMinWeeks]).map((w) => (
                    <option key={w} value={w}>
                      {w} semaines
                    </option>
                  ))}
                </Select>
              )}
              <p className="mt-1 text-xs text-gray-400">
                Si la compétition est trop proche pour la durée minimale du plan, l'athlète peut valider une préparation condensée
                après avertissement, jusqu'à cette durée. En dessous, il devra redéfinir son objectif.
              </p>
            </div>
          )}
        </div>
      </Card>

      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
          {/* Cycle library rail */}
          <Card className="flex max-h-[70vh] flex-col gap-3 overflow-hidden">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Bibliothèque de cycles</p>
            <Input placeholder="Rechercher un cycle" value={search} onChange={(e) => setSearch(e.target.value)} />
            <p className="text-xs text-gray-400">
              Glissez un cycle dans la séquence — un même cycle peut être utilisé deux fois.
            </p>
            <div className="flex flex-col gap-2 overflow-y-auto pr-1">
              {(rail?.items ?? []).length === 0 && <p className="text-sm text-gray-400">Aucun cycle.</p>}
              {(rail?.items ?? []).map((c) => (
                <RailCard
                  key={c.id}
                  id={c.id}
                  name={c.name}
                  minWeeks={c.minWeekNumber}
                  maxWeeks={c.maxWeekNumber}
                  used={c.usedInCount > 0}
                />
              ))}
            </div>
          </Card>

          {/* Sequence + result-by-format */}
          <div className="flex flex-col gap-4">
            <SequenceZone
              rows={rows}
              meta={meta}
              alloc={alloc}
              summary={
                planType === 'generic'
                  ? `Durée : ${GENERIC_WEEKS} semaines requises · actuel ${dmax} · ${rows.length} cycle(s)`
                  : `Préparation : ${dmin} à ${dmax} semaines${
                      planType === 'spe' && condensedMinWeeks !== null ? ` · condensée dès ${condensedMinWeeks} semaines` : ''
                    }${recovery > 0 ? ` · + ${recovery} sem. de récupération` : ''} · ${rows.length} cycle(s)`
              }
              onOpenLength={setLengthUid}
              onMove={moveRow}
              onRemove={removeRow}
              onPreview={planId && !dirty ? () => navigate(`/simulator?planId=${planId}`) : undefined}
            />

            <AllocationBars alloc={alloc} thresholdWeeks={planType === 'spe' ? condensedMinWeeks : null} />
            <GenerationCheckPanel planId={planId} dirty={dirty} />
          </div>
        </div>

        <DragOverlay>
          {draggingMeta && (
            <div className="rounded-lg border border-brand-500 bg-white p-2 text-sm font-medium shadow-lg dark:bg-gray-900">
              {draggingMeta.name}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      <CycleLengthModal
        row={lengthRow}
        position={lengthPosition}
        meta={lengthRow ? meta[lengthRow.cycleId] : undefined}
        formatIdentifier={formatIdentifier}
        onClose={() => setLengthUid(null)}
        onApply={setWeekCount}
        onRemove={(uid) => {
          removeRow(uid);
          setLengthUid(null);
        }}
      />
    </>
  );
};

const SequenceZone = ({
  rows,
  meta,
  alloc,
  summary,
  onOpenLength,
  onMove,
  onRemove,
  onPreview,
}: {
  rows: SequenceRow[];
  meta: Record<string, CycleMeta>;
  alloc: CycleAllocation[];
  summary: string;
  onOpenLength: (uid: string) => void;
  onMove: (uid: string, dir: -1 | 1) => void;
  onRemove: (uid: string) => void;
  onPreview?: () => void;
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: 'seq' });

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Séquence du plan</p>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{summary}</span>
          {onPreview && (
            <Button variant="outline" size="sm" onClick={onPreview}>
              Aperçu semaine par semaine
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {rows.map((r, i) => {
          const m = meta[r.cycleId];
          const weeks = alloc[i]?.effectiveMaxWeeks ?? 0;
          const cap = r.weekCount === null ? 'plafond : max' : `plafond : ${r.weekCount} semaines`;
          const badge = m ? ROLE_BADGES[m.role] : undefined;
          return (
            <div
              key={r.uid}
              className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {m?.name ?? 'Cycle'}
                  {badge && (
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badge.className}`}
                      title={badge.title}
                    >
                      {badge.label}
                    </span>
                  )}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {Array.from({ length: weeks }, (_, k) => (
                      <span
                        key={k}
                        className="h-2 w-4 rounded-sm"
                        style={{ backgroundColor: planCycleColor(i) }}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">
                    {m ? `${m.minWeekNumber}–${m.maxWeekNumber} semaines` : ''} · {cap} · {weeks} sem. max
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onMove(r.uid, -1)}
                  disabled={i === 0}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800"
                  aria-label="Monter"
                >
                  <ArrowUpIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onMove(r.uid, 1)}
                  disabled={i === rows.length - 1}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800"
                  aria-label="Descendre"
                >
                  <ArrowDownIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onOpenLength(r.uid)}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label="Longueur & position"
                >
                  <Cog6ToothIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onRemove(r.uid)}
                  className="rounded p-1 text-error-500 hover:bg-error-500/10"
                  aria-label="Retirer"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div
        ref={setNodeRef}
        className={clsx(
          'rounded-lg border border-dashed p-4 text-center text-sm transition-colors',
          isOver
            ? 'border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-950'
            : 'border-gray-300 text-gray-400 dark:border-gray-700'
        )}
      >
        Déposer un cycle ici · position {rows.length + 1}
      </div>
    </Card>
  );
};

export default PlanComposerPage;
